import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import type { ColorType } from '@lib/types';
import { setThisClass } from '@lib/utils';

type StepsOrientation = 'horizontal' | 'vertical';
type StepStatus = 'upcoming' | 'current' | 'completed';

type StepsContextType = {
  currentStep: number;
  orientation: StepsOrientation;
  color: ColorType;
};

const StepsContext = createContext<StepsContextType | null>(null);

function useStepsContext() {
  const context = useContext(StepsContext);
  if (!context) {
    throw new Error('Step components must be used within a Steps');
  }
  return context;
}

export type StepsProps = {
  children: ReactNode;
  currentStep: number;
  orientation?: StepsOrientation;
  color?: ColorType;
  className?: string;
};

export type StepProps = {
  children?: ReactNode;
  title: string;
  description?: string;
  icon?: ReactNode;
  index?: number;
  status?: StepStatus;
  onClick?: () => void;
  className?: string;
};

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="size-5"
  >
    <path
      fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
      clipRule="evenodd"
    />
  </svg>
);

function Steps({
  children,
  currentStep,
  orientation = 'horizontal',
  color = 'primary',
  className,
}: StepsProps) {
  return (
    <StepsContext.Provider value={{ currentStep, orientation, color }}>
      <div
        className={clsx(
          'flex',
          orientation === 'vertical' ? 'flex-col gap-0' : 'flex-row items-start gap-0',
          className
        )}
      >
        {children}
      </div>
    </StepsContext.Provider>
  );
}

function Step({
  children,
  title,
  description,
  icon,
  index = 0,
  status: propStatus,
  onClick,
  className,
}: StepProps) {
  const { currentStep, orientation, color } = useStepsContext();

  const status: StepStatus =
    propStatus ||
    (index < currentStep ? 'completed' : index === currentStep ? 'current' : 'upcoming');

  const isClickable = onClick !== undefined;

  return (
    <div
      className={clsx(
        'flex flex-1',
        orientation === 'vertical' ? 'flex-row gap-4' : 'flex-col items-center gap-2',
        className
      )}
    >
      <div className={clsx('flex items-center', orientation === 'horizontal' && 'w-full')}>
        {/* Connector before */}
        {index > 0 && orientation === 'horizontal' && (
          <div
            className={clsx(
              'flex-1 h-0.5',
              status === 'upcoming'
                ? 'bg-gray-200 dark:bg-dark-600'
                : [setThisClass(color), 'bg-this dark:bg-this-light']
            )}
          />
        )}

        {/* Step indicator */}
        <button
          type="button"
          onClick={onClick}
          disabled={!isClickable}
          className={clsx(
            'relative z-10 flex items-center justify-center rounded-full shrink-0 transition-colors',
            status === 'completed' && [
              setThisClass(color),
              'size-8 bg-this text-white dark:bg-this-light',
            ],
            status === 'current' && [
              setThisClass(color),
              'size-8 border-2 border-this bg-white dark:border-this-light dark:bg-dark-800',
            ],
            status === 'upcoming' &&
              'size-8 border-2 border-gray-300 bg-white dark:border-dark-500 dark:bg-dark-800',
            isClickable && 'cursor-pointer hover:opacity-80',
            !isClickable && 'cursor-default'
          )}
        >
          {status === 'completed'
            ? icon || <CheckIcon />
            : icon || (
                <span
                  className={clsx(
                    'text-sm font-medium',
                    status === 'current' && [setThisClass(color), 'text-this dark:text-this-light'],
                    status === 'upcoming' && 'text-gray-500 dark:text-dark-400'
                  )}
                >
                  {index + 1}
                </span>
              )}
        </button>

        {/* Connector after (horizontal only) */}
        {orientation === 'horizontal' && (
          <div
            className={clsx(
              'flex-1 h-0.5',
              status === 'completed'
                ? [setThisClass(color), 'bg-this dark:bg-this-light']
                : 'bg-gray-200 dark:bg-dark-600'
            )}
          />
        )}
      </div>

      {/* Content */}
      <div className={clsx(orientation === 'vertical' ? 'flex-1 pb-8' : 'text-center')}>
        <p
          className={clsx(
            'font-medium',
            status === 'current' && [setThisClass(color), 'text-this dark:text-this-light'],
            status === 'completed' && 'text-gray-900 dark:text-dark-100',
            status === 'upcoming' && 'text-gray-500 dark:text-dark-400'
          )}
        >
          {title}
        </p>
        {description && (
          <p className="text-sm text-gray-500 dark:text-dark-400 mt-1">{description}</p>
        )}
        {children && <div className="mt-2">{children}</div>}
      </div>

      {/* Vertical connector */}
      {orientation === 'vertical' && (
        <div
          className={clsx(
            'absolute left-4 top-8 bottom-0 w-0.5 -translate-x-1/2',
            status === 'completed'
              ? [setThisClass(color), 'bg-this dark:bg-this-light']
              : 'bg-gray-200 dark:bg-dark-600'
          )}
        />
      )}
    </div>
  );
}

Steps.displayName = 'Steps';
Step.displayName = 'Step';

export { Steps, Step };
